import type { ItemCarrinho } from '@/lib/pricing';

/**
 * Estado e redutor do carrinho — F4.3.1. Puro, sem React, sem efeito colateral.
 *
 * Está separado do contexto de propósito: o mesmo redutor roda no teste unitário
 * sem montar árvore de componente, e a regra de teto de quantidade fica num
 * lugar só. O contexto (`CartContext.tsx`) apenas embrulha isto com `useReducer`.
 *
 * O carrinho NUNCA guarda preço. Só slug e quantidade. O preço é derivado do
 * catálogo vigente na hora de exibir (`useCartPricing`) e recalculado do zero
 * no servidor na hora de cobrar (regra de ouro 2).
 */

/**
 * Teto por item. Não existe compra legítima de 11 unidades do mesmo exame para
 * o mesmo paciente — acima disso é erro de digitação ou robô, e cada unidade
 * ocupa vaga de coleta real.
 */
export const MAX_QUANTIDADE_POR_ITEM = 10;

/** Teto de exames distintos no carrinho. */
export const MAX_ITENS_CARRINHO = 30;

export interface CartState {
  itens: ItemCarrinho[];
  /** Unidade de coleta escolhida. Nula até o cliente decidir. */
  unidadeSlug: string | null;
  /** Cupom digitado, sempre normalizado em maiúsculas. */
  cupom: string | null;
  /**
   * Contador de revisão, incrementado a cada mudança real.
   * Ação que não altera nada devolve o MESMO objeto de estado e não incrementa,
   * o que deixa React pular a re-renderização e a persistência pular a escrita.
   */
  versao: number;
}

export type CartAction =
  | { type: 'adicionar'; exameSlug: string; quantidade?: number }
  | { type: 'remover'; exameSlug: string }
  | { type: 'definirQuantidade'; exameSlug: string; quantidade: number }
  | { type: 'definirUnidade'; unidadeSlug: string | null }
  | { type: 'definirCupom'; cupom: string | null }
  | { type: 'limpar' }
  | { type: 'hidratar'; estado: CartState };

export const ESTADO_INICIAL: CartState = {
  itens: [],
  unidadeSlug: null,
  cupom: null,
  versao: 0,
};

/** Prende a quantidade entre 1 e o teto por item. */
function limitarQuantidade(quantidade: number): number {
  return Math.min(MAX_QUANTIDADE_POR_ITEM, Math.max(1, Math.floor(quantidade)));
}

/**
 * Normaliza cupom para maiúsculas sem espaço nas pontas.
 * O motor de preço compara o código em maiúsculas; normalizar aqui evita que
 * "  natal10 " deixe de casar com a regra "NATAL10" e o cliente ache que o
 * cupom não existe.
 */
function normalizarCupom(cupom: string | null): string | null {
  if (cupom == null) return null;
  const limpo = cupom.trim().toUpperCase();
  return limpo === '' ? null : limpo;
}

/**
 * Higieniza uma lista de itens de origem não confiável.
 *
 * Usada na hidratação: o que vem de `localStorage` pode ter sido editado à mão,
 * ter quantidade negativa, slug duplicado ou 900 itens. Deduplica somando as
 * quantidades, aplica os tetos e descarta o que não é utilizável.
 */
export function sanitizarItens(itens: readonly ItemCarrinho[]): ItemCarrinho[] {
  const porSlug = new Map<string, number>();

  for (const item of itens) {
    if (typeof item?.exameSlug !== 'string') continue;
    const slug = item.exameSlug.trim();
    if (slug === '') continue;

    const quantidade = Math.floor(Number(item.quantidade));
    if (!Number.isFinite(quantidade) || quantidade <= 0) continue;

    // Slug repetido vira soma, não duplicata — o carrinho tem uma linha por exame.
    porSlug.set(slug, limitarQuantidade((porSlug.get(slug) ?? 0) + quantidade));

    if (porSlug.size >= MAX_ITENS_CARRINHO) break;
  }

  return [...porSlug].map(([exameSlug, quantidade]) => ({ exameSlug, quantidade }));
}

/** Aplica a mudança e incrementa a revisão. */
function proximo(state: CartState, mudanca: Partial<Omit<CartState, 'versao'>>): CartState {
  return { ...state, ...mudanca, versao: state.versao + 1 };
}

export function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'adicionar': {
      const slug = action.exameSlug.trim();
      if (slug === '') return state;

      const pedida = Math.floor(action.quantidade ?? 1);
      if (!Number.isFinite(pedida) || pedida <= 0) return state;

      const atual = state.itens.find((i) => i.exameSlug === slug);

      if (atual) {
        // Adicionar item já presente incrementa; nunca cria segunda linha.
        const nova = limitarQuantidade(atual.quantidade + pedida);
        if (nova === atual.quantidade) return state;
        return proximo(state, {
          itens: state.itens.map((i) => (i.exameSlug === slug ? { ...i, quantidade: nova } : i)),
        });
      }

      if (state.itens.length >= MAX_ITENS_CARRINHO) return state;

      return proximo(state, {
        itens: [...state.itens, { exameSlug: slug, quantidade: limitarQuantidade(pedida) }],
      });
    }

    case 'remover': {
      const restantes = state.itens.filter((i) => i.exameSlug !== action.exameSlug);
      if (restantes.length === state.itens.length) return state;
      return proximo(state, { itens: restantes });
    }

    case 'definirQuantidade': {
      const atual = state.itens.find((i) => i.exameSlug === action.exameSlug);
      if (!atual) return state;

      const pedida = Math.floor(action.quantidade);

      // Quantidade zero ou menos remove: é o passo final do decremento no
      // seletor de quantidade, e o cliente espera que o item suma.
      if (!Number.isFinite(pedida) || pedida <= 0) {
        return proximo(state, {
          itens: state.itens.filter((i) => i.exameSlug !== action.exameSlug),
        });
      }

      const nova = limitarQuantidade(pedida);
      if (nova === atual.quantidade) return state;

      return proximo(state, {
        itens: state.itens.map((i) =>
          i.exameSlug === action.exameSlug ? { ...i, quantidade: nova } : i,
        ),
      });
    }

    case 'definirUnidade': {
      if (state.unidadeSlug === action.unidadeSlug) return state;
      return proximo(state, { unidadeSlug: action.unidadeSlug });
    }

    case 'definirCupom': {
      const cupom = normalizarCupom(action.cupom);
      if (state.cupom === cupom) return state;
      return proximo(state, { cupom });
    }

    case 'limpar': {
      if (state.itens.length === 0 && state.unidadeSlug === null && state.cupom === null) {
        return state;
      }
      return { ...ESTADO_INICIAL, versao: state.versao + 1 };
    }

    case 'hidratar': {
      // Fronteira não confiável: o estado vem de armazenamento local.
      // A revisão continua a da sessão viva, não a que veio do disco.
      return {
        itens: sanitizarItens(action.estado.itens ?? []),
        unidadeSlug: action.estado.unidadeSlug ?? null,
        cupom: normalizarCupom(action.estado.cupom ?? null),
        versao: state.versao + 1,
      };
    }

    default: {
      // Ação nova sem tratamento vira erro de compilação aqui.
      action satisfies never;
      return state;
    }
  }
}

/** Soma das quantidades — é o número que aparece no contador do cabeçalho. */
export function quantidadeTotal(itens: readonly ItemCarrinho[]): number {
  return itens.reduce((total, i) => total + i.quantidade, 0);
}
